var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountSummaryV2Query } from 'syf-api/accountSummary';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import queryParamsToObject from './queryParamsToObject';
import { errorCodes, errorCodesNumber } from '../../../const/errorCodes';
import { STORE_KEYS } from '../../../const/store-keys';
import useEligibilityQuery from '../../../services/eligibility/eligibility-query';
import Eligibility from '../eligibility';
import Execution from '../execution';
import CBRContext from '../context';
import store from '../../../helpers/store';
import { CardContent, CardMain, CardWrapper } from '../subcomponents';
import { analyticsValues, triggerAnalyticsKeys } from '../../../const/analytics';
import { CardContainer } from '../eligibility/subcomponents';
var CreditBalanceRefundCard = function (_a) {
    var _b, _c, _d;
    var authenticationRedirect = _a.authenticationRedirect;
    var t = useTranslation().t;
    var _e = useState(false), isCbrExecution = _e[0], setIsCbrExecution = _e[1];
    var _f = useState(''), otpErrorCode = _f[0], setOtpErrorCode = _f[1];
    var _g = useContext(CBRContext), config = _g.config, accountId = _g.accountId, triggerAnalyticsEvent = _g.triggerAnalyticsEvent;
    var accountSummary = useAccountSummaryV2Query({
        config: config,
        keyProps: {
            accountId: accountId,
            accountIdType: 'guid'
        },
        queryOptions: {
            enabled: Boolean(accountId)
        }
    }).data;
    var currentBalance = ((_b = accountSummary === null || accountSummary === void 0 ? void 0 : accountSummary.credit) === null || _b === void 0 ? void 0 : _b.currentBalance) || '0.00';
    var isSetPay = Boolean(((_c = accountSummary === null || accountSummary === void 0 ? void 0 : accountSummary.product) === null || _c === void 0 ? void 0 : _c.type) === 'INSTALLMENT_LOAN');
    if (accountId) {
        var selectedAccountInfo = {
            currentBalance: currentBalance,
            isSetPay: isSetPay
        };
        store.set("cbr-mfe_".concat(accountId), selectedAccountInfo, 'session');
    }
    var stopExecution = function () {
        var _a;
        store.remove(STORE_KEYS.isCbrExecution, 'session');
        store.remove(STORE_KEYS.isProfileEnabled, 'session');
        setIsCbrExecution(false);
        triggerAnalyticsEvent((_a = {},
            _a[triggerAnalyticsKeys.pageKind] = analyticsValues.cbr,
            _a[triggerAnalyticsKeys.pageFunction] = analyticsValues.manageCreditBalance,
            _a[triggerAnalyticsKeys.pageName] = analyticsValues.view,
            _a));
    };
    var startExecution = function () {
        store.set(STORE_KEYS.isCbrExecution, true, 'session');
        setIsCbrExecution(true);
    };
    useEffect(function () {
        var otpErrorCallbackParams = store.get('otpErrorCallbackParams', 'session');
        if (otpErrorCallbackParams) {
            var errorObjectParams = queryParamsToObject(otpErrorCallbackParams);
            var stepUpError = '';
            /* istanbul ignore else -- @preserve */
            if ('error' in errorObjectParams) {
                stepUpError = errorObjectParams.error;
            }
            if (stepUpError !== errorCodes.accessDenied) {
                store.remove('otpErrorCallbackParams', 'session');
            }
            // Reset profile and execution values from session
            stopExecution();
            setOtpErrorCode(stepUpError);
        }
        else {
            // No errors from OTP stepup, return to refund execution page
            var refundExecutionStarted = store.get(STORE_KEYS.isCbrExecution, 'session');
            setIsCbrExecution(refundExecutionStarted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /*
     * After coming back from Stepup, Context data will be having the initial values
     * Therefore, we need to get the account info from the storage
     */
    var accountBalance = currentBalance;
    var isSetPayAccount = isSetPay;
    if (!accountId) {
        var savedAccountId = store.get(STORE_KEYS.selectedIdKey, 'session');
        var accountInfo = store.get("cbr-mfe_".concat(savedAccountId), 'session');
        accountBalance = accountInfo === null || accountInfo === void 0 ? void 0 : accountInfo.currentBalance;
        isSetPayAccount = accountInfo === null || accountInfo === void 0 ? void 0 : accountInfo.isSetPay;
    }
    else {
        var selectedAccountInfo = {
            currentBalance: currentBalance,
            isSetPay: isSetPay
        };
        store.set("cbr-mfe_".concat(accountId), selectedAccountInfo, 'session');
    }
    var selectedAccountId = accountId !== null && accountId !== void 0 ? accountId : store.get(STORE_KEYS.selectedIdKey, 'session');
    var _h = useEligibilityQuery({
        config: config,
        keyProps: {
            accountId: selectedAccountId,
            accountIdType: 'guid'
        },
        queryOptions: {
            refetchOnMount: 'always'
        }
    }), eligibilityData = _h.data, isEligibilityFetching = _h.isFetching;
    var eligibilityResponseData = eligibilityData;
    var statusCode = eligibilityResponseData === null || eligibilityResponseData === void 0 ? void 0 : eligibilityResponseData.statusCode;
    var businessErrors = (eligibilityResponseData === null || eligibilityResponseData === void 0 ? void 0 : eligibilityResponseData.businessErrors) || [];
    var isBusinessErrorStatusCode = Boolean(statusCode === 422);
    var businessErrorCode = (_d = businessErrors === null || businessErrors === void 0 ? void 0 : businessErrors[0]) === null || _d === void 0 ? void 0 : _d.code;
    var isBusinessError = isBusinessErrorStatusCode &&
        businessErrors &&
        businessErrorCode !== errorCodesNumber.refundInProgress;
    var isAddressChangeErrorCode = businessErrorCode === errorCodesNumber.changeAdress;
    var isAccessDenied = otpErrorCode === errorCodes.accessDenied;
    var isOtherBusinessError = Boolean(statusCode) && !isBusinessErrorStatusCode;
    var isSubmitted = isBusinessErrorStatusCode && !isBusinessError;
    var cardTitle = t('title.manageBalanceOption');
    return (_jsx(CardWrapper, { children: _jsx(CardMain, __assign({ title: cardTitle, weight: "bold" }, { children: isEligibilityFetching ? (_jsx(CardContainer, { children: _jsx(VerticalCardLoader, {}) })) : (_jsx(CardContent, { children: isCbrExecution && !isAddressChangeErrorCode ? (_jsx(Execution, { submittedSubText: t('text.requestRefundCheck'), accountIdType: "guid", currentBalance: accountBalance, stopExecution: stopExecution, authenticationRedirect: authenticationRedirect })) : (_jsx(Eligibility, { submittedSubText: t('text.requestRefundCheck'), currentBalance: accountBalance, isRefundEligible: eligibilityResponseData === null || eligibilityResponseData === void 0 ? void 0 : eligibilityResponseData.refundEligible, isBusinessError: isBusinessError, isAccessDenied: isAccessDenied, isOtherBusinessError: isOtherBusinessError, isSubmitted: isSubmitted, isSetPay: isSetPayAccount, startExecution: startExecution })) })) })) }));
};
export default CreditBalanceRefundCard;
